import { AppearanceSource } from '@app/modules/persons/appearances/types';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { STREAM_UI_CONFIG } from '../../../../consts';
import {
  TRACK_INTERVAL_BAR_RADIUS_PX,
  TRACK_MINI_INTERVAL_BAR_RADIUS_PX,
} from '../../../../stream-track-visual-tokens';

/** Minimum rendered bar width so short intervals stay clickable. */
export const MIN_APPEARANCE_BAR_WIDTH_PX = 3;

/** Face / voice bar height inside a per-person lane row. */
export const LANE_BAR_HEIGHT_PX = 12;

/**
 * Thin voice underline when a lane has both face and voice.
 * Must stay in sync with {@link getLaneBarLayout} offset math.
 */
export const MIXED_VOICE_UNDERLINE_HEIGHT_PX = 4;

/** Face / voice bar height inside a summary-strip sub-row. */
export const SUMMARY_BAR_HEIGHT_PX = 10;

/** Vertical stride between summary sub-rows (bar + gap); leaves inset top/bottom. */
export const SUMMARY_ROW_STRIDE_PX = 12;

/** @see TRACK_INTERVAL_BAR_RADIUS_PX — Personas track / summary appearance bars. */
export const APPEARANCE_BAR_RADIUS_PX = TRACK_INTERVAL_BAR_RADIUS_PX;

/** @see TRACK_MINI_INTERVAL_BAR_RADIUS_PX — Personas table mini-timeline bars. */
export const MINI_TIMELINE_BAR_RADIUS_PX = TRACK_MINI_INTERVAL_BAR_RADIUS_PX;

/** Cast-cluster avatar size in the summary strip. */
export const CAST_AVATAR_PX = 32;

/** Identity avatar size inside a per-person lane row. */
export const LANE_AVATAR_PX = 22;

/** Opacity for non-hovered bars when a cast/lane hover is active. */
export const APPEARANCE_BAR_DIMMED_OPACITY = 0.22;

const CONTENT_INSET_Y = STREAM_UI_CONFIG.trackContentInsetY;

type LaneBarLayout = {
  height: number;
  offsetY: number;
};

type SummaryBarLayout = {
  height: number;
  offsetY: number;
};

/**
 * Shared solid / hatched styles for appearance bars (track, summary strip, mini timeline).
 */
export function appearanceBarSx(color: string, source: AppearanceSource): SystemStyleObject<Theme> {
  const isFace = source === 'face';
  return {
    borderRadius: `${APPEARANCE_BAR_RADIUS_PX}px`,
    cursor: 'pointer',
    boxSizing: 'border-box',
    bgcolor: isFace ? color : 'transparent',
    border: isFace ? 'none' : `1px solid ${color}`,
    backgroundImage: isFace
      ? 'none'
      : `repeating-linear-gradient(135deg, ${color} 0 3px, transparent 3px 6px)`,
  };
}

/**
 * Hover emphasis for appearance bars: lane-color outline on the active person,
 * harder dim on everyone else.
 */
export function appearanceBarEmphasisSx({
  dimmed,
  emphasized,
  color,
}: {
  dimmed: boolean;
  emphasized: boolean;
  color: string;
}): SystemStyleObject<Theme> {
  return {
    opacity: dimmed ? APPEARANCE_BAR_DIMMED_OPACITY : 1,
    outline: emphasized ? `1px solid ${color}` : 'none',
    outlineOffset: emphasized ? '1px' : 0,
    position: 'relative',
    zIndex: emphasized ? 2 : 1,
    transition: 'opacity 120ms, outline-color 120ms',
  };
}

/**
 * Height + vertical offset for a bar inside a per-person lane.
 * Single-source: centered inside the inset band. Mixed: face above, voice as a thin underline.
 */
export function getLaneBarLayout(
  source: AppearanceSource,
  isMixed: boolean,
  laneHeight: number,
): LaneBarLayout {
  const contentHeight = laneHeight - 2 * CONTENT_INSET_Y;

  if (!isMixed) {
    return {
      height: LANE_BAR_HEIGHT_PX,
      offsetY: CONTENT_INSET_Y + (contentHeight - LANE_BAR_HEIGHT_PX) / 2,
    };
  }

  if (source === 'face') {
    return {
      height: LANE_BAR_HEIGHT_PX,
      offsetY: CONTENT_INSET_Y,
    };
  }

  return {
    height: MIXED_VOICE_UNDERLINE_HEIGHT_PX,
    offsetY: laneHeight - CONTENT_INSET_Y - MIXED_VOICE_UNDERLINE_HEIGHT_PX,
  };
}

/** Height + vertical offset for a packed bar inside the summary strip. */
export function getSummaryBarLayout(row: number): SummaryBarLayout {
  return {
    height: SUMMARY_BAR_HEIGHT_PX,
    offsetY: CONTENT_INSET_Y + row * SUMMARY_ROW_STRIDE_PX,
  };
}
